<template>
  <div class="gravity-waves-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex> \$2\$ </div>
    </div>

    <div class="main-layout vertical">
      <!-- 仿真演示区 -->
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>应变振幅 (h)</label>
            <span class="val">{{ (amplitude * 1e-21).toExponential(2) }}</span>
          </div>
          <div class="hud-item pink">
            <label>轨道频率 (f)</label>
            <span class="val">{{ (rotationSpeed * 2).toFixed(1) }} <small>Hz</small></span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 400 240" class="wave-canvas">
          <!-- 动态时空背景网格 -->
          <g v-for="i in 11" :key="'gx'+i">
            <path :d="gridPath(i, 'x')" fill="none" :stroke="gridColor" :stroke-width="0.5" opacity="0.3" />
          </g>
          <g v-for="i in 7" :key="'gy'+i">
            <path :d="gridPath(i, 'y')" fill="none" :stroke="gridColor" :stroke-width="0.5" opacity="0.3" />
          </g>

          <!-- 双星并合波源 -->
          <g :transform="`translate(200, 120) rotate(${angle})`">
            <circle cx="-15" cy="0" r="8" fill="#000" stroke="#fff" stroke-width="2" />
            <circle cx="15" cy="0" r="8" fill="#000" stroke="#fff" stroke-width="2" />
            <!-- 吸积盘效果 -->
            <ellipse cx="0" cy="0" rx="30" ry="5" fill="none" stroke="#6366f1" opacity="0.4" stroke-dasharray="4,4" />
          </g>

          <!-- 探测信号监测 (Chirp Signal) -->
          <path :d="waveformPath" fill="none" stroke="#f43f5e" stroke-width="2" transform="translate(50, 210)" />
          <text x="50" y="200" fill="#94a3b8" font-size="10" font-weight="700">LIGO INTERFEROMETER SIGNAL (CHIRP)</text>
        </svg>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card-grid">
          <div class="control-item">
            <div class="label-row">
              <span class="label">黑洞质量 (M)</span>
              <span class="val">{{ mass }} M☉</span>
            </div>
            <el-slider v-model="mass" :min="10" :max="100" size="small"></el-slider>
          </div>
          <div class="control-item">
            <div class="label-row">
              <span class="label">演化进度 (Time)</span>
              <span class="val">{{ (time * 10).toFixed(0) }} ms</span>
            </div>
            <el-slider v-model="timeOffset" :min="1" :max="10" :step="0.1" size="small"></el-slider>
          </div>
        </div>

        <div class="discovery-card-compact" v-latex>
          <i class="el-icon-info"></i>
          <p><strong>物理规律：</strong>引力波是时空的波动。当双黑洞相互旋进并合时，它们会搅动周围时空产生向外传播的涟漪。这种波动极其微弱，LIGO 激光干涉仪能探测到相当于质子直径千分之一的<strong>距离改变 (ΔL)</strong>，开启了引力波天文学的新纪元。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GravityWavesViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      angle: 0,
      mass: 40,
      time: 0,
      timeOffset: 1,
      running: true
    }
  },
  computed: {
    amplitude() { return (this.mass / 20) * this.timeOffset; },
    rotationSpeed() { return 2 + this.timeOffset * 0.5; },
    gridColor() {
       return this.timeOffset > 7 ? '#6366f1' : '#1e293b';
    },
    waveformPath() {
      let d = "M 0 0 ";
      const segments = 60;
      for (let i = 0; i <= segments; i++) {
        let x = i * 5;
        // Chirp: frequency and amplitude increase with time
        let chirpFreq = 0.05 + (i * 0.002);
        let y = this.amplitude * 8 * Math.sin(i * chirpFreq * 10 - this.time * 10);
        d += `L ${x} ${y} `;
      }
      return d;
    }
  },
  methods: {
    gridPath(i, type) {
      let amp = this.amplitude * 4;
      let phase = this.time * 8;
      if (type === 'x') {
        let xBase = i * 40;
        let d = `M ${xBase} 0 `;
        for (let y = 0; y <= 240; y += 15) {
          let distToCenter = Math.abs(y - 120) / 120;
          let stretch = amp * (1 - distToCenter) * Math.cos(y * 0.04 - phase);
          d += `L ${xBase + stretch} ${y} `;
        }
        return d;
      } else {
        let yBase = i * 40;
        let d = `M 0 ${yBase} `;
        for (let x = 0; x <= 400; x += 15) {
          let distToCenter = Math.abs(x - 200) / 200;
          let stretch = -amp * (1 - distToCenter) * Math.cos(x * 0.04 - phase);
          d += `L ${x} ${yBase + stretch} `;
        }
        return d;
      }
    },
    update() {
      if (!this.running) return;
      this.time += 0.02;
      this.angle = (this.angle + this.rotationSpeed) % 360;
      this.frameId = requestAnimationFrame(this.update);
    }
  },
  mounted() { this.update(); },
  beforeDestroy() {
    this.running = false;
    cancelAnimationFrame(this.frameId);
  }
}
</script>

<style scoped lang="scss">
.gravity-waves-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }

.viz-header-compact {
  display: flex; justify-content: space-between; align-items: center; padding: 0 4px;
  .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; }
  .formula-chip { background: #eef2ff; color: #4f46e5; border: 1px solid #c7d2fe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; }
}

.simulation-stage {
  flex: 1; min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden;
  .hud-panel {
    position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10;
    .hud-item {
      background: rgba(15, 23, 42, 0.8); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px;
      label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; }
      .val { font-size: 14px; color: #f1f5f9; font-weight: 800; font-family: monospace; }
      &.cyan { border-left: 3px solid #06b6d4; }
      &.pink { border-left: 3px solid #f43f5e; }
    }
  }
}

.wave-canvas { display: block; filter: drop-shadow(0 0 10px rgba(99, 102, 241, 0.1)); }

.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px; background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0;
  .control-item {
    .label-row { display: flex; justify-content: space-between; .label { font-size: 12px; color: #64748b; } .val { font-size: 12px; color: #2563eb; font-weight: 700; } }
  }
}

.discovery-card-compact {
  background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 10px;
  i { color: #4f46e5; font-size: 1.1rem; }
  p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } }
}
</style>
